import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HardHat, Search, Plus, Trash2, X, Filter, FileText, FileSpreadsheet,
  RefreshCcw, ChevronDown, ChevronUp, Upload, Download,
  FileOutput, LayoutGrid, CheckCircle, AlertCircle,
  Wrench, TrendingUp, Shield, Calendar, MapPin, Hash, Tag
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { useTranslation } from 'react-i18next';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { getBrandLocale } from '../../shared/utils/brandCurrency';
import localApiAdapter from '../../core/services/localApiAdapter';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import {sliceQawaemStats, QAWAEM_KPI_GRID_CLASS, QAWAEM_TABLE_ACTIONS_TH_CLASS, QawaemTableSelectAllHeader, QawaemTableSelectRowCell, createArticleStatusChangeHandler, mapStatusOptionsToColumnFilter, useQawaemColumnVisibility, QawaemTableStickyActionsTd } from './articlesListPageUtils';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemErpCrudModal from '../../products/saudi/components/QawaemErpCrudModal';
import { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import { useModuleConfigTheme } from '../../shared/hooks/useModuleConfigTheme';
import { AppConfirmDialog } from '../../shared/components/AppConfirmDialog';
import { useCrudDeleteConfirm } from '../../shared/hooks/useCrudDeleteConfirm';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useArticlesConfig } from './useArticlesConfig';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
// Tables-mobile.css - DELETED (not needed)

const EquipmentPage = () => {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  useArticlesConfig();
  const cfgTheme = useModuleConfigTheme();
  const isLight = cfgTheme.light;
  const p = (key, opts) => t(`modules.article.pages.equipmentPage.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const [searchTerm, setSearchTerm] = useState('');
  const [equipment, setEquipment] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [viewingEquipment, setViewingEquipment] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Form data with collapsible sections
  const [formData, setFormData] = useState({
    name: '', serial_number: '', asset_tag: '', location: '', equipment_type: '', purchase_date: '',
    warranty_expiry: '', last_maintenance_date: '', next_maintenance_date: '',
    status: '', notes: ''
  });

  const [formTab, setFormTab] = useState('info');

  // Advanced Options states
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_equipment');
  const [quickFilter, setQuickFilter] = useState('all');

  // Sorting state
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const {
    deleteConfirm,
    closeDeleteConfirm,
    openDeleteOne,
    openBulkDelete,
    takePendingIds,
  } = useCrudDeleteConfirm();
  const entityLabel = p('entity_label', { defaultValue: 'المعدة' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'معدة' });

  // Dropdown states and refs
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  useEffect(() => {
    loadEquipment();
  }, [refreshTrigger]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadEquipment = async () => {
    try {
      const response = await localApiAdapter.get('/api/articles/equipment');
      const data = response.data || response;
      setEquipment(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error loading equipment:', error);
      setEquipment([]);
    }
  };

  const computeNextAssetTag = useCallback(() => {
    const prefix = getEntityPrefix(settings, 'equipment', 'ASSET');
    return generateEntityReference(prefix, equipment, (item) => item.asset_tag);
  }, [equipment, settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert(p('required_name'));
      return;
    }

    try {
      const assetTag = (formData.asset_tag || '').trim() || computeNextAssetTag();
      const payload = { ...formData, asset_tag: assetTag };
      if (editingEquipment) {
        await localApiAdapter.put(`/api/articles/equipment/${editingEquipment.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/api/articles/equipment', payload);
        notifyCreated(showNotify, entityLabel);
      }
      resetForm();
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      notifyError(showNotify, p('save_error'));
    }
  };

  const handleEdit = (equip) => {
    setEditingEquipment(equip);
    setFormData({
      name: equip.name,
      serial_number: equip.serial_number || '',
      asset_tag: equip.asset_tag || '',
      location: equip.location || '',
      equipment_type: equip.equipment_type || '',
      purchase_date: equip.purchase_date ? equip.purchase_date.substring(0, 10) : '',
      warranty_expiry: equip.warranty_expiry ? equip.warranty_expiry.substring(0, 10) : '',
      last_maintenance_date: equip.last_maintenance_date ? equip.last_maintenance_date.substring(0, 10) : '',
      next_maintenance_date: equip.next_maintenance_date ? equip.next_maintenance_date.substring(0, 10) : '',
      status: equip.status,
      notes: equip.notes || ''
    });
    setFormTab('info');
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingEquipment(null);
    setFormData({
      name: '', serial_number: '', asset_tag: computeNextAssetTag(), location: '', equipment_type: '', purchase_date: '',
      warranty_expiry: '', last_maintenance_date: '', next_maintenance_date: '',
      status: '', notes: ''
    });
    setFormTab('info');
    setShowModal(true);
  };

  

  const handleDelete = (id) => openDeleteOne(id);

  const handleBulkDelete = () => {
    if (!selectedEquipment.length) return;
    openBulkDelete(selectedEquipment);
  };

  const executeDeleteConfirmed = async () => {
    const ids = takePendingIds();
    if (!ids.length) return;
    try {
      if (ids.length === 1) {
        await localApiAdapter.delete(`/api/articles/equipment/${ids[0]}`);
        notifyDeleted(showNotify, entityLabel);
        setRefreshTrigger((prev) => prev + 1);
      } else {
        await Promise.all(ids.map((id) => localApiAdapter.delete(`/api/articles/equipment/${id}`)));
        notifyBulkDeleted(showNotify, ids.length, entityLabelPlural);
        setSelectedEquipment([]);
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Error deleting equipment:', error);
      notifyError(showNotify, p('save_error'));
    }
  };

  const handleView = (equip) => {
    setViewingEquipment(equip);
    setShowViewModal(true);
  };

  const STATUS_OPTIONS = useMemo(
    () => [
      { value: 'operational', label: p('status_operational'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
      { value: 'under_maintenance', label: p('status_maintenance'), icon: Wrench, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
      { value: 'out_of_service', label: p('status_out_of_service'), icon: AlertCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    ],
    [t],
  );

  const statusColumnFilterOptions = useMemo(
    () => mapStatusOptionsToColumnFilter(STATUS_OPTIONS),
    [STATUS_OPTIONS],
  );

  const handleStatusChange = useCallback(
    createArticleStatusChangeHandler('/api/articles/equipment', () => setRefreshTrigger((prev) => prev + 1)),
    [],
  );

  const resetForm = () => {
    setFormData({
      name: '', serial_number: '', asset_tag: '', location: '', equipment_type: '', purchase_date: '',
      warranty_expiry: '', last_maintenance_date: '', next_maintenance_date: '',
      status: '', notes: ''
    });
    setEditingEquipment(null);
    setFormTab('info');
    setShowModal(false);
  };

  const formTabs = useMemo(() => ([
    { id: 'info', label: p('section_info'), icon: HardHat },
    { id: 'details', label: p('section_details'), icon: MapPin },
  ]), [p]);

  const handleSelectEquipment = (id) => {
    setSelectedEquipment(selectedEquipment.includes(id) ? selectedEquipment.filter(eId => eId !== id) : [...selectedEquipment, id]);
  };

  // Calculate stats
  const equipmentArray = Array.isArray(equipment) ? equipment : [];
  const stats = {
    total: equipmentArray.length,
    operational: equipmentArray.filter(e => e.status === 'operational').length,
    under_maintenance: equipmentArray.filter(e => e.status === 'under_maintenance').length,
    out_of_service: equipmentArray.filter(e => e.status === 'out_of_service').length,
    critical: equipmentArray.filter(e => {
      if (!e.next_maintenance_date) return false;
      const daysUntil = Math.floor((new Date(e.next_maintenance_date) - new Date()) / (1000 * 60 * 60 * 24));
      return daysUntil <= 7;
    }).length
  };

  // Main view tabs
  const mainTabs = [
    { id: 'all_equipment', name: p('tab_general'), icon: LayoutGrid },
    { id: 'by_status', name: p('tab_by_status'), icon: Shield },
    { id: 'by_maintenance', name: p('tab_by_type'), icon: Wrench }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: HardHat },
    { id: 'operational', name: p('filter_operational'), icon: CheckCircle },
    { id: 'under_maintenance', name: p('filter_maintenance'), icon: Wrench },
    { id: 'out_of_service', name: p('filter_out_of_service'), icon: AlertCircle }
  ];

  const equipmentDistribution = {
    all_equipment: {
      title: p('overview_title'),
      data: [
        { name: p('status_operational'), count: stats.operational, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { name: p('status_maintenance'), count: stats.under_maintenance, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" },
        { name: p('status_out_of_service'), count: stats.out_of_service, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    },
    by_status: {
      title: p('distribution_by_status'),
      data: [
        { name: p('status_operational'), count: stats.operational, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { name: p('status_maintenance'), count: stats.under_maintenance, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" },
        { name: p('status_out_of_service'), count: stats.out_of_service, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    },
    by_maintenance: {
      title: p('distribution_by_type'),
      data: [
        { name: p('status_operational'), count: equipment.filter(eq => eq.next_maintenance_date && new Date(eq.next_maintenance_date) > new Date()).length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { name: p('status_critical'), count: stats.critical, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    }
  };

  const distributionInfo = equipmentDistribution[mainViewMode] || equipmentDistribution.all_equipment;

  const baseFilteredEquipment = useMemo(
    () =>
      equipmentArray.filter((equip) => {
        const matchesSearch =
          !searchTerm ||
          equip.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          equip.serial_number?.toLowerCase().includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;
        if (quickFilter === 'operational' && equip.status !== 'operational') return false;
        if (quickFilter === 'under_maintenance' && equip.status !== 'under_maintenance') return false;
        if (quickFilter === 'out_of_service' && equip.status !== 'out_of_service') return false;
        return true;
      }),
    [equipmentArray, searchTerm, quickFilter],
  );

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('name', (r) => r.name || ''),
      textColumnRule('maintenance', (r) =>
        r.next_maintenance_date
          ? new Date(r.next_maintenance_date).toLocaleDateString(dateLocale)
          : 'N/A',
      ),
      textColumnRule('serial_number', (r) => r.serial_number || ''),
      textColumnRule('asset_tag', (r) => r.asset_tag || ''),
      textColumnRule('location', (r) => r.location || ''),
      textColumnRule('warranty_expiry', (r) => (r.warranty_expiry ? new Date(r.warranty_expiry).toLocaleDateString(dateLocale) : '')),
      exactColumnRule('status', (r) => r.status || ''),
    ],
    [dateLocale],
  );

  const filteredEquipment = useMemo(
    () => applyColumnFilters(baseFilteredEquipment, colFilters, columnFilterRules),
    [baseFilteredEquipment, colFilters, columnFilterRules],
  );

  const filteredEquipmentIds = useMemo(() => filteredEquipment.map((e) => e.id), [filteredEquipment]);
  const allRowsSelected = filteredEquipmentIds.length > 0 && filteredEquipmentIds.every((id) => selectedEquipment.includes(id));
  const someRowsSelected = selectedEquipment.length > 0 && !allRowsSelected;
  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) setSelectedEquipment([]);
    else setSelectedEquipment(filteredEquipmentIds);
  };

  const getStatusBadge = (status) => {
    const config = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
    const { label, icon: Icon, style } = config;
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}>
        <Icon size={14} />
        {label}
      </span>
    );
  };

  const { columnVisibility, handleToggleColumn, showCol, visibleDataColCount } = useQawaemColumnVisibility([
    'name', 'serial_number', 'asset_tag', 'location', 'maintenance', 'warranty_expiry', 'status',
  ]);

  const qawaemColumns = useMemo(
    () => [
      { key: 'name', label: p('field_name'), checked: columnVisibility.name !== false, required: true },
      { key: 'serial_number', label: p('col_serial'), checked: columnVisibility.serial_number !== false },
      { key: 'asset_tag', label: p('col_asset_tag'), checked: columnVisibility.asset_tag !== false },
      { key: 'location', label: p('field_location'), checked: columnVisibility.location !== false },
      { key: 'maintenance', label: p('section_maintenance'), checked: columnVisibility.maintenance !== false },
      { key: 'warranty_expiry', label: p('col_warranty'), checked: columnVisibility.warranty_expiry !== false },
      { key: 'status', label: t('common.status'), checked: columnVisibility.status !== false },
      { key: 'actions', label: t('common.actions'), checked: true, required: true },
    ],
    [t, p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked).length;
  const tableColSpan = 1 + visibleDataColCount + 1;

  return (
    <div className={`space-y-6 employees-container`}>
      {/* Header - BLUE THEME */}
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700">
              <HardHat className="h-8 w-8 text-white" />
            </div>
            {p('title')}
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${
              showAdvancedOptions
                ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30'
                : 'bg-white/5 border-white/20'
            } border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
            title={p('advanced_options')}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          <button
            onClick={openCreateModal}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2 shadow-lg shadow-blue-500/30"
          >
            <Plus className="h-5 w-5" />
            <span>{p('add_button')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={QAWAEM_KPI_GRID_CLASS}>
        {sliceQawaemStats([
          { title: p('stat_total'), value: stats.total, icon: HardHat, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_operational'), value: stats.operational, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('stat_maintenance'), value: stats.under_maintenance, icon: Wrench, color: 'from-yellow-500 to-yellow-600' },
          { title: p('stat_out_of_service'), value: stats.out_of_service, icon: AlertCircle, color: 'from-red-500 to-red-600' },
          { title: p('stat_critical'), value: stats.critical, icon: TrendingUp, color: 'from-orange-500 to-orange-600' }
        ], 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options - Collapsible */}
      <motion.div
        initial={false}
        animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{ overflow: "hidden" }}
      >
        <div className="space-y-6 pt-6">
          {/* Main Tabs */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); }}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    mainViewMode === tab.id
                      ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Quick Filters */}
          {mainViewMode === 'all_equipment' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => setQuickFilter(filter.id)}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                      quickFilter === filter.id
                        ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <filter.icon className="h-5 w-5" />
                    <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Distribution Overview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <h3 className="text-xl font-semibold text-white mb-4">{distributionInfo.title}</h3>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item, index) => (
                <div key={index} className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-white">{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full">
                    <div
                      className={`h-3 rounded-full ${item.color}`}
                      style={{ width: `${stats.total > 0 ? (item.count / stats.total * 100) : 0}%` }}
                    ></div>
                  </div>
                  <p className="mt-2 text-sm text-white/60">
                    {stats.total > 0 ? (item.count / stats.total * 100).toFixed(1) : 0}{p('percent_of_total')}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Table Section */}
      <div className="glass-panel glass-table-shell overflow-visible">
          {/* Bulk Actions */}
          {selectedEquipment.length > 0 && (
            <div className="flex items-center gap-2 bg-white/10 py-2 px-4 rounded-xl mt-4">
              <span className="text-white font-medium">{p('selected_count', { count: selectedEquipment.length })}</span>
              <button
                onClick={handleBulkDelete}
                className="bg-red-500/20 text-red-400 p-2 rounded-lg hover:bg-red-500/30 transition-colors flex items-center gap-1"
              >
                <Trash2 size={18} />
                <span>{t('common.delete')}</span>
              </button>
            </div>
          )}

        <div className="overflow-x-auto table-horizontal-scroll">
          <table className="w-full employees-table data-table">
            <thead className="glass-table-head">
              <tr>
                <QawaemTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onChange={handleSelectAll} />
                {showCol('name') ? (
                  <TableColumnHeaderFilter columnKey="name" label={p('field_name')} value={colFilters.name ?? ''} onChange={(v) => setColFilter('name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} />
                ) : null}
                {showCol('serial_number') ? <TableColumnHeaderFilter columnKey="serial_number" label={p('col_serial')} value={colFilters.serial_number ?? ''} onChange={(v) => setColFilter('serial_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                {showCol('asset_tag') ? <TableColumnHeaderFilter columnKey="asset_tag" label={p('col_asset_tag')} value={colFilters.asset_tag ?? ''} onChange={(v) => setColFilter('asset_tag', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                {showCol('location') ? <TableColumnHeaderFilter columnKey="location" label={p('field_location')} value={colFilters.location ?? ''} onChange={(v) => setColFilter('location', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                {showCol('maintenance') ? (
                  <TableColumnHeaderFilter columnKey="maintenance" label={p('section_maintenance')} value={colFilters.maintenance ?? ''} onChange={(v) => setColFilter('maintenance', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} />
                ) : null}
                {showCol('warranty_expiry') ? <TableColumnHeaderFilter columnKey="warranty_expiry" label={p('col_warranty')} value={colFilters.warranty_expiry ?? ''} onChange={(v) => setColFilter('warranty_expiry', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                {showCol('status') ? <TableColumnHeaderFilter columnKey="status" label={t('common.status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" /> : null}
                <QawaemTableHeaderActionsCell label={t('common.actions')} columns={qawaemColumns} onToggle={handleToggleColumn} visibleCount={qawaemVisibleColumnCount} totalCount={qawaemColumns.length} thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS} />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10 employees-table-body">
              {filteredEquipment.length > 0 ? (
                filteredEquipment.map((equip, index) => (
                  <tr
                    key={equip.id}
                    className={`hover:bg-white/5 transition-colors ${selectedEquipment.includes(equip.id) ? 'bg-white/10' : ''}`}
                  >
                      <QawaemTableSelectRowCell checked={selectedEquipment.includes(equip.id)} onChange={() => handleSelectEquipment(equip.id)} dataLabel={p('label_select')} />
                    {showCol('name') ? (
                      <td className="px-6 py-4 text-white font-semibold" data-label={p('field_name')}>{equip.name}</td>
                    ) : null}
                    {showCol('serial_number') ? (
                      <td className="px-6 py-4 text-white/80 text-sm" data-label={p('col_serial')}>{equip.serial_number || p('na')}</td>
                    ) : null}
                    {showCol('asset_tag') ? (
                      <td className="px-6 py-4 text-white/80 text-sm" data-label={p('col_asset_tag')}>{equip.asset_tag || p('na')}</td>
                    ) : null}
                    {showCol('location') ? (
                      <td className="px-6 py-4 text-white/80" data-label={p('field_location')}>{equip.location || p('na')}</td>
                    ) : null}
                    {showCol('maintenance') ? (
                      <td className="px-6 py-4 text-white/80" data-label={p('data_label_next_maintenance')}>{equip.next_maintenance_date ? new Date(equip.next_maintenance_date).toLocaleDateString(dateLocale) : 'N/A'}</td>
                    ) : null}
                    {showCol('warranty_expiry') ? (
                      <td className="px-6 py-4 text-white/80" data-label={p('col_warranty')}>{equip.warranty_expiry ? new Date(equip.warranty_expiry).toLocaleDateString(dateLocale) : 'N/A'}</td>
                    ) : null}
                    {showCol('status') ? (
                      <td className="px-6 py-4 text-center" data-label={p('field_status')}>
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge status={equip.status} recordId={equip.id} options={STATUS_OPTIONS} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })} />
                        </div>
                      </td>
                    ) : null}
                    <QawaemTableStickyActionsTd>
                      <QawaemErpRowActions
                        onView={() => handleView(equip)}
                        onEdit={() => handleEdit(equip)}
                        onDelete={() => handleDelete(equip.id)}
                        viewTitle={p('view_details')}
                        editTitle={p('btn_edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </QawaemTableStickyActionsTd>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                    <HardHat size={48} className="mx-auto mb-4 text-white/30" />
                    <p className="text-lg">{p('empty_title')}</p>
                    <p className="text-sm mt-2">{p('empty_subtitle')}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <QawaemErpCrudModal
        open={showModal}
        onClose={resetForm}
        title={editingEquipment ? p('modal_edit_title') : p('modal_create_title')}
        icon={HardHat}
        formId="equipment-form"
        saveLabel={editingEquipment ? t('common.save') : t('common.create')}
        showSaveButton
        testId="equipment-crud-modal"
      >
        <div className="-m-6 flex flex-col min-h-[320px]">
          <div className={`shrink-0 px-6 pt-3 border-b ${isLight ? 'border-gray-200' : 'border-white/10'}`}>
            <div className="flex flex-wrap gap-1" role="tablist" aria-label={editingEquipment ? p('modal_edit_title') : p('modal_create_title')}>
              {formTabs.map((tab) => {
                const TabIcon = tab.icon;
                const active = formTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setFormTab(tab.id)}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${
                      active
                        ? (isLight
                          ? 'bg-[#6e9b6e]/15 text-forest-900 border-b-2 border-[#6e9b6e] -mb-px'
                          : 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px')
                        : (isLight
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80'
                          : 'text-white/55 hover:text-white hover:bg-white/5')
                    }`}
                  >
                    <TabIcon size={16} className={active ? (tab.id === 'details' ? 'text-blue-400' : 'text-emerald-400') : ''} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="overflow-y-auto p-6 flex-1 min-h-0">
            <form id="equipment-form" onSubmit={handleSubmit}>
              {formTab === 'info' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_asset_tag')}</label>
                    <input type="text" value={formData.asset_tag} onChange={(e) => setFormData({ ...formData, asset_tag: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('field_name_required')} /></label>
                    <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('field_name_placeholder')} />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_equipment_type')}</label>
                    <CustomSelect
                      value={formData.equipment_type}
                      onChange={(value) => setFormData({ ...formData, equipment_type: value })}
                      options={[
                        { value: 'Machines Industrielles', label: p('eq_type_industrial'), isDefault: false },
                        { value: 'Systèmes de Climatisation', label: p('eq_type_hvac'), isDefault: false },
                        { value: 'Équipements Électriques', label: p('eq_type_electrical'), isDefault: false },
                        { value: 'Systèmes de Plomberie', label: p('eq_type_plumbing'), isDefault: false },
                        { value: 'Équipements de Production', label: p('eq_type_production'), isDefault: false },
                        { value: 'Systèmes de Sécurité', label: p('eq_type_security'), isDefault: false },
                        { value: 'Équipements Informatiques', label: p('eq_type_it'), isDefault: false },
                        { value: 'Machines de Levage', label: p('eq_type_lifting'), isDefault: false },
                        { value: 'Systèmes de Ventilation', label: p('eq_type_ventilation'), isDefault: false },
                        { value: 'Équipements de Soudage', label: p('eq_type_welding'), isDefault: false },
                        { value: 'Compresseurs', label: p('eq_type_compressors'), isDefault: false },
                        { value: 'Générateurs', label: p('eq_type_generators'), isDefault: false },
                        { value: 'Pompes', label: p('eq_type_pumps'), isDefault: false },
                        { value: 'Moteurs', label: p('eq_type_motors'), isDefault: false },
                        { value: 'Transformateurs', label: p('eq_type_transformers'), isDefault: false }
                      ]}
                      placeholder={p('select_type_placeholder')}
                      searchable={true}
                      allowCreate={true}
                      createLabel={p('create_type_label')}
                      autoSelect={false}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_serial')}</label>
                    <input type="text" value={formData.serial_number} onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('field_serial_placeholder')} />
                  </div>
                </div>
              )}

              {formTab === 'details' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_location')}</label>
                    <input type="text" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('field_location_placeholder')} />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_status')}</label>
                    <CustomSelect
                      value={formData.status}
                      onChange={(value) => setFormData({ ...formData, status: value })}
                      options={[
                        { value: 'operational', label: p('status_option_operational'), isDefault: false },
                        { value: 'under_maintenance', label: p('status_option_under_maintenance'), isDefault: false },
                        { value: 'out_of_service', label: p('status_option_out_of_service'), isDefault: false }
                      ]}
                      placeholder={p('select_status_placeholder')}
                      autoSelect={false}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_purchase_date')}</label>
                    <CustomDatePicker
                      value={formData.purchase_date}
                      onChange={(dateString) => setFormData({ ...formData, purchase_date: dateString })}
                      placeholder={p('date_placeholder')}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_warranty_expiry')}</label>
                    <CustomDatePicker
                      value={formData.warranty_expiry}
                      onChange={(dateString) => setFormData({ ...formData, warranty_expiry: dateString })}
                      placeholder={p('date_placeholder')}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_asset_tag')}</label>
                    <input type="text" value={formData.asset_tag} onChange={(e) => setFormData({ ...formData, asset_tag: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('section_notes')}</label>
                    <input type="text" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('field_notes_placeholder')} />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_last_maintenance')}</label>
                    <CustomDatePicker
                      value={formData.last_maintenance_date}
                      onChange={(dateString) => setFormData({ ...formData, last_maintenance_date: dateString })}
                      placeholder={p('date_placeholder')}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_next_maintenance')}</label>
                    <CustomDatePicker
                      value={formData.next_maintenance_date}
                      onChange={(dateString) => setFormData({ ...formData, next_maintenance_date: dateString })}
                      placeholder={p('date_placeholder')}
                    />
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </QawaemErpCrudModal>

      {showViewModal && viewingEquipment ? (
      <QawaemErpViewModal
        open
        onClose={() => { setShowViewModal(false); setViewingEquipment(null); }}
        title={viewingEquipment?.name ?? p('view_title')}
        icon={HardHat}
        maxWidth="1100px"
        testId="equipment-view-modal"
      >
        <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <HardHat className="h-5 w-5 text-emerald-400" />
                  <h3 className="text-white font-semibold">{p('section_info')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><p className="text-white/60 text-sm">{p('field_name')}</p><p className="text-white font-medium">{viewingEquipment.name}</p></div>
                  <div><p className="text-white/60 text-sm">{p('field_equipment_type')}</p><p className="text-white">{viewingEquipment.equipment_type || p('na')}</p></div>
                  <div><p className="text-white/60 text-sm">{p('col_serial')}</p><p className="text-white">{viewingEquipment.serial_number || p('na')}</p></div>
                  <div><p className="text-white/60 text-sm">{p('col_asset_tag')}</p><p className="text-white">{viewingEquipment.asset_tag || p('na')}</p></div>
                  <div><p className="text-white/60 text-sm">{p('field_last_maintenance')}</p><p className="text-white">{viewingEquipment.last_maintenance_date ? new Date(viewingEquipment.last_maintenance_date).toLocaleDateString(dateLocale) : 'N/A'}</p></div>
                  <div><p className="text-white/60 text-sm">{p('field_next_maintenance')}</p><p className="text-white">{viewingEquipment.next_maintenance_date ? new Date(viewingEquipment.next_maintenance_date).toLocaleDateString(dateLocale) : 'N/A'}</p></div>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="h-5 w-5 text-blue-400" />
                  <h3 className="text-white font-semibold">{p('section_details')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><p className="text-white/60 text-sm">{p('field_location')}</p><p className="text-white">{viewingEquipment.location || p('na')}</p></div>
                  <div><p className="text-white/60 text-sm mb-2">{p('field_status')}</p><div className="flex items-start">{getStatusBadge(viewingEquipment.status)}</div></div>
                  <div><p className="text-white/60 text-sm">{p('field_purchase_date')}</p><p className="text-white">{viewingEquipment.purchase_date ? new Date(viewingEquipment.purchase_date).toLocaleDateString(dateLocale) : 'N/A'}</p></div>
                  <div><p className="text-white/60 text-sm">{p('field_warranty_expiry')}</p><p className="text-white">{viewingEquipment.warranty_expiry ? new Date(viewingEquipment.warranty_expiry).toLocaleDateString(dateLocale) : 'N/A'}</p></div>
                </div>
              </div>

              {viewingEquipment.notes && (
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center gap-3 mb-4">
                    <FileText className="h-5 w-5 text-red-400" />
                    <h3 className="text-white font-semibold">{p('section_notes')}</h3>
                  </div>
                  <p className="text-white">{viewingEquipment.notes}</p>
                </div>
              )}
            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <button onClick={() => { setShowViewModal(false); setViewingEquipment(null); handleEdit(viewingEquipment); }} className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/30">{p('btn_edit')}</button>
              <button onClick={() => { setShowViewModal(false); setViewingEquipment(null); }} className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all duration-300">{p('btn_close')}</button>
            </div>
        </div>
      </QawaemErpViewModal>
      ) : null}
            <AppConfirmDialog
        open={deleteConfirm.open}
        title={t('common.delete', 'حذف')}
        message={
          deleteConfirm.ids.length > 1
            ? p('bulk_delete_confirm', { count: deleteConfirm.ids.length })
            : p('delete_confirm')
        }
        confirmLabel={t('common.delete', 'حذف')}
        cancelLabel={t('common.cancel', 'إلغاء')}
        danger
        onCancel={closeDeleteConfirm}
        onConfirm={executeDeleteConfirmed}
      />
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default EquipmentPage;

